import React from 'react'
import PropTypes from 'prop-types'
import { flowRight } from 'lodash'
import { withTranslationCustom } from 'lib/i18next'
import { withRouter } from 'react-router-dom'
import * as Nope from 'lib/utils/validation/Nope'
import { withErrorBoundary } from 'lib/errorBoundary'

import { Field, Form } from 'formik'
import ExtendedFormik from 'components/Forms/ExtendedFormik'
import VehiclePriceEstimateLogic from 'components/Vehicles/VehiclePriceEstimate/VehiclePriceEstimateLogic'
import Button from 'components/UI/Button'
import FormElementGroup from 'components/FormElements/FormElementGroup'
import Input from 'components/Forms/FormElements/Input'
import FormikAutoSubmit from 'components/Forms/FormikAutoSubmit'
import Loading from 'components/Util/Loading'

import './vehicle-price-estimate-form.pcss'

const validationSchema = Nope.schema({
    mileage: Nope.positiveInteger()
        .required('formElements:validation.required'),
})

const VehiclePriceEstimateForm = ({ t, vehicle, initialValues, onBeforeSubmit, onComplete, onFailed, newVehicle }) => {
    const validate = (values) => {
        try {
            return validationSchema.validateSync(values)
        } catch (e) {
            return false
        }
    }

    return (
        <div className="vehicle-price-estimate-form">
            <VehiclePriceEstimateLogic>
                {({ getPricePrediction, loading }) => (
                    <ExtendedFormik
                        id="vehicle-price-estimate-form"
                        validationSchema={validationSchema}
                        initialValues={{
                            mileage: '',
                            ...initialValues,
                        }}
                        onSubmit={async (values, actions) => {
                            onBeforeSubmit && onBeforeSubmit(values)
                            const addToTimeline = !!newVehicle
                            await getPricePrediction(vehicle.id, parseInt(values.mileage), addToTimeline)
                                .then((result) => {
                                    actions.setSubmitting(false)
                                    if (result.data.pricePrediction.valid) {
                                        onComplete && onComplete(result)
                                    } else {
                                        onFailed && onFailed()
                                    }
                                })
                                .catch(() => {
                                    actions.setSubmitting(false)
                                    onFailed && onFailed()
                                })
                        }}
                    >
                        {({ isSubmitting, isValid, submitForm, values }) => (
                            <Form noValidate>
                                <Loading loading={isSubmitting}>
                                    {() => (
                                        <FormElementGroup>
                                            <h1 className="react-modal__modal-title">{t('vehicles:pricePrediction.modalTitle')}</h1>
                                            <Field
                                                type="number"
                                                name="mileage"
                                                label={t('vehicles:pricePrediction.formFields.mileage')}
                                                unitsTip="km"
                                                component={Input}
                                                disabled={loading}
                                                required
                                            />
                                            <div className="action-wrapper">
                                                <Button
                                                    type="submit"
                                                    className="button"
                                                    disabled={isSubmitting}
                                                    loading={isSubmitting}
                                                    trackName="submit"
                                                >
                                                    {t('common:get')}
                                                </Button>
                                            </div>
                                        </FormElementGroup>
                                    )}
                                </Loading>
                                <FormikAutoSubmit
                                    conditionOnMount={() => validate(values)}
                                    submitForm={submitForm}
                                />
                            </Form>
                        )}
                    </ExtendedFormik>
                )}
            </VehiclePriceEstimateLogic>
        </div>
    )
}

VehiclePriceEstimateForm.propTypes = {
    vehicle: PropTypes.object.isRequired,
    onBeforeSubmit: PropTypes.func.isRequired,
    onComplete: PropTypes.func.isRequired,
    onFailed: PropTypes.func.isRequired,
    mileage: PropTypes.string,
    newVehicle: PropTypes.bool,
}

export default flowRight(
    withErrorBoundary,
    withRouter,
    withTranslationCustom('vehicle'),
)(VehiclePriceEstimateForm)
